import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import EPageHeader from '@/components/everridge/EPageHeader';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminRoomMainTab from '@/features/superAdmin/features/data/rooms/components/SuperAdminRoomMainTab';
import SuperAdminDataPaginatedTable from '@/features/superAdmin/features/data/shared/components/SuperAdminDataPaginatedTable';
import {
    roomAmenityNestedColumns,
    roomPhotoNestedColumns,
} from '@/features/superAdmin/features/data/rooms/components/SuperAdminRoomsPageColumns';
import { useSuperAdminRoomDetailApi } from '@/features/superAdmin/features/data/rooms/hooks/useSuperAdminRoomDetailApi';

type RoomDetailTab = 'main' | 'amenities' | 'photos';

const SuperAdminRoomDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { apartment, isLoading } = useSuperAdminRoomDetailApi(id);
    const [activeTab, setActiveTab] = useState<RoomDetailTab>('main');

    if (userRole !== Roles.superAdmin) return null;

    const goBack = () => navigate('/superAdmin/data/rooms');

    const tabs: { id: RoomDetailTab; label: string }[] = [
        { id: 'main', label: t('superAdmin.data.rooms.tabs.main') },
        { id: 'amenities', label: t('superAdmin.data.rooms.tabs.amenities') },
        { id: 'photos', label: t('superAdmin.data.rooms.tabs.photos') },
    ];

    const pageTitle = apartment?.name
        ? `${t('superAdmin.data.rooms.detail.title')} — ${apartment.name}`
        : t('superAdmin.data.rooms.detail.title');

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={pageTitle}>
                <Button variant="outline" className="gap-2" onClick={goBack}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.rooms.backToList')}
                </Button>
            </EPageHeader>

            {isLoading && !apartment ? (
                <div className="flex flex-1 items-center justify-center py-12">
                    <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
            ) : !apartment ? (
                <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6 text-sm">
                    {t('elements.table.body.empty')}
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2 border-b border-popover-100 pb-2">
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                variant={activeTab === tab.id ? 'default' : 'outline'}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>

                    {activeTab === 'main' && <SuperAdminRoomMainTab apartment={apartment} />}

                    {activeTab === 'amenities' && (
                        <SuperAdminDataPaginatedTable
                            columns={roomAmenityNestedColumns}
                            data={apartment.amenities}
                        />
                    )}

                    {activeTab === 'photos' && (
                        <SuperAdminDataPaginatedTable
                            columns={roomPhotoNestedColumns}
                            data={apartment.photos}
                        />
                    )}
                </>
            )}
        </section>
    );
};

export default SuperAdminRoomDetailPage;
